<script lang="ts">
  import Modal from './Modal.svelte';
  import {
    dialogStore,
    resolveCurrent,
    cancelCurrent,
  } from '../ui/dialogs.svelte';

  /**
   * Hôte unique des dialogs impératifs (alert/confirm/prompt).
   *
   * À monter UNE SEULE FOIS, idéalement dans App.svelte au niveau racine.
   * Lit `dialogStore` (singleton) et rend le dialog courant via <Modal>.
   *
   * **Aucun style dans ce composant** — tout passe par les classes
   * globales : `.btn`/`.btn--*` (btn.css), `.input`/`.field__error`
   * (input.css), `.hint` (input.css). Le scoping Svelte est inutile ici
   * puisqu'on ne crée que des classes globales.
   */

  let promptValue = $state('');
  let promptError = $state('');

  // GARDE ANTI-CRASH : le <dialog> natif peut émettre close/cancel APRÈS la
  // résolution du dialog (current → null). Les getters de props de <Modal>
  // sont ré-évalués à ce moment-là — ils ne doivent JAMAIS déréférencer null
  // (« Cannot read properties of null (reading 'type') », prod 10/07/2026).
  const dlgType = $derived(dialogStore.current?.type ?? null);
  const dlgTitle = $derived(dialogStore.current?.opts.title ?? '');

  $effect(() => {
    const s = dialogStore.current;
    if (!s) {
      promptError = '';
      return;
    }
    if (s.type === 'prompt') {
      promptValue = s.opts.defaultValue ?? '';
      promptError = '';
    }
  });

  function onCancel(): void {
    cancelCurrent();
  }

  function onConfirm(): void {
    const s = dialogStore.current;
    if (!s) return;
    if (s.type === 'alert') {
      resolveCurrent();
    } else if (s.type === 'confirm') {
      resolveCurrent(true);
    } else {
      const value = promptValue;
      if (s.opts.validate) {
        const err = s.opts.validate(value);
        if (err) {
          promptError = err;
          return;
        }
      }
      resolveCurrent(value);
    }
  }

  function onPromptKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onConfirm();
    }
  }
</script>

{#if dialogStore.current}
  {@const s = dialogStore.current}
  <Modal
    open={true}
    title={dlgTitle}
    showClose={dlgType !== 'confirm'}
    blocking={dlgType === 'confirm'}
    onClose={onCancel}
  >
    {#if s.type === 'alert' || s.type === 'confirm'}
      <p>{s.opts.message}</p>
    {:else}
      {#if s.opts.message}
        <p>{s.opts.message}</p>
      {/if}
      <label class="field" style:margin-top="var(--space-3)">
        <input
          type="text"
          class="input"
          bind:value={promptValue}
          placeholder={s.opts.placeholder ?? ''}
          onkeydown={onPromptKeydown}
          aria-invalid={!!promptError}
        />
        {#if promptError}
          <span class="field__error">{promptError}</span>
        {/if}
      </label>
    {/if}

    {#snippet actions()}
      {#if s.type === 'alert'}
        <button type="button" class="btn btn--primary" onclick={onConfirm}>
          {s.opts.okLabel ?? 'OK'}
        </button>
      {:else if s.type === 'confirm'}
        <button type="button" class="btn btn--ghost" onclick={onCancel}>
          {s.opts.cancelLabel ?? 'Annuler'}
        </button>
        <button
          type="button"
          class={'btn ' + (s.opts.variant === 'danger' ? 'btn--danger' : 'btn--primary')}
          onclick={onConfirm}
        >
          {s.opts.confirmLabel ?? 'Confirmer'}
        </button>
      {:else}
        <button type="button" class="btn btn--ghost" onclick={onCancel}>
          {s.opts.cancelLabel ?? 'Annuler'}
        </button>
        <button type="button" class="btn btn--primary" onclick={onConfirm}>
          {s.opts.okLabel ?? 'Valider'}
        </button>
      {/if}
    {/snippet}
  </Modal>
{/if}
